import {
  Menubar,
  MenubarContent,
  MenubarItem,
  MenubarMenu,
  MenubarTrigger,
} from "@/components/ui/menubar";
import { useTranslation } from "react-i18next";
import { Loader2, UserCog } from "lucide-react";
import { useUsersContext } from "../context";
import { useModulePermissions } from "@/modules/account/hooks/useModulePermissions";
import { Module } from "@/constants";
import useAccount from "@/modules/account/hooks/useAccount";
import { useState, useMemo, useCallback } from "react";
import ImpersonationConfirmDialog from "./ImpersonationConfirmDialog";
import { PasswordResetByAdminConfirmDialog } from "@/components/PasswordResetByAdminConfirmDialog";
import { useResetUserPassword } from "@/hooks/useResetUserPassword";

export const UsersMenu = () => {
  const { t } = useTranslation();
  const {
    data: user,
    loadingState,
    handleActivate,
    handleDeactivate,
  } = useUsersContext();
  const { can_update, users_impersonate, users_password_reset } =
    useModulePermissions(Module.Users);
  const { onImpersonate, account, impersonateCognitoId } = useAccount();
  const [impersonating, setImpersonating] = useState(false);

  const resetUserPassword = useResetUserPassword(user?.id ?? "");
  const {
    isAnyActionPending,
    canActivateUser,
    canDeactivateUser,
    canImpersonateUser,
  } = useMemo(() => {
    return {
      isAnyActionPending:
        loadingState.isLoading || impersonating || resetUserPassword.isPending,
      canActivateUser: can_update && user && !user.is_active,
      canDeactivateUser: can_update && user && user.is_active,
      canImpersonateUser:
        users_impersonate &&
        user &&
        account &&
        user.id !== account.id &&
        !impersonateCognitoId,
    };
  }, [
    loadingState.isLoading,
    impersonating,
    resetUserPassword.isPending,
    can_update,
    user,
    users_impersonate,
    account,
    impersonateCognitoId,
  ]);

  const handleImpersonate = useCallback(async () => {
    if (!user) return;
    setImpersonating(true);
    try {
      await onImpersonate(user.cognito_id);
    } finally {
      setImpersonating(false);
    }
  }, [user, onImpersonate]);

  if ((!can_update && !users_impersonate && !users_password_reset) || !user) {
    return null;
  }

  return (
    <Menubar className="mb-2">
      <MenubarMenu>
        <MenubarTrigger>{t("users.menubar.trigger")}</MenubarTrigger>
        <MenubarContent>
          {canDeactivateUser && (
            <MenubarItem
              onClick={handleDeactivate}
              disabled={isAnyActionPending}
            >
              {isAnyActionPending && (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              )}
              {t("users.menubar.deactivate")}
            </MenubarItem>
          )}

          {canActivateUser && (
            <MenubarItem onClick={handleActivate} disabled={isAnyActionPending}>
              {isAnyActionPending && (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              )}
              {t("users.menubar.activate")}
            </MenubarItem>
          )}

          {users_password_reset && user && (
            <PasswordResetByAdminConfirmDialog
              onConfirm={resetUserPassword.onConfirm}
            >
              <MenubarItem
                onSelect={(e) => e.preventDefault()}
                disabled={isAnyActionPending}
              >
                {t("users.menubar.resetPassword")}
              </MenubarItem>
            </PasswordResetByAdminConfirmDialog>
          )}

          {canImpersonateUser && (
            <ImpersonationConfirmDialog onConfirm={handleImpersonate}>
              <MenubarItem
                onSelect={(e) => e.preventDefault()}
                disabled={isAnyActionPending}
              >
                {impersonating && (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                )}
                {!impersonating && <UserCog className="mr-2 h-4 w-4" />}
                {t("users.menubar.impersonate")}
              </MenubarItem>
            </ImpersonationConfirmDialog>
          )}
        </MenubarContent>
      </MenubarMenu>
    </Menubar>
  );
};
